import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClipboardCheck, Loader2, CheckCircle2, XCircle, MapPin, IndianRupee, Paperclip, Clock, User2 } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "completed", label: "Pending review", tone: "text-amber-700 border-amber-500" },
  { key: "approved",  label: "Approved",       tone: "text-emerald-700 border-emerald-500" },
  { key: "rejected",  label: "Rejected",       tone: "text-rose-700 border-rose-500" },
];

export default function AdminVisits() {
  const [tab, setTab] = useState("completed");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/visits?status=${tab}`);
      setRows(data);
    } finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => rows.length, [rows]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 inline-flex items-center gap-2">
          <ClipboardCheck className="w-6 h-6 text-indigo-600" /> Visit Reviews
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Approve or reject Op Manager visit reports, expenses, and field photos.</p>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-visits-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === t.key ? t.tone : "text-slate-500 border-transparent hover:text-slate-700"}`}
          >
            {t.label}{tab === t.key && counts > 0 ? ` (${counts})` : ""}
          </button>
        ))}
      </div>

      {loading && <div className="py-10 text-center"><Loader2 className="w-5 h-5 mx-auto text-slate-400 animate-spin" /></div>}
      {!loading && rows.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center">
          <ClipboardCheck className="w-10 h-10 text-slate-300 mx-auto" />
          <div className="mt-3 font-semibold text-slate-800">Nothing here yet</div>
          <div className="text-xs text-slate-500 mt-1">Op Manager visit reports will show up as they land.</div>
        </div>
      )}

      <div className="grid gap-2">
        {rows.map((v) => (
          <article
            key={v.id}
            data-testid={`admin-visit-${v.id}`}
            className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md cursor-pointer transition-shadow"
            onClick={() => setDetail(v)}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 inline-flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-slate-500" /> {v.outlet_code} · {v.outlet_name}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 flex items-center flex-wrap gap-x-2">
                  <span className="inline-flex items-center gap-1"><User2 className="w-3 h-3" /> {v.op_manager_name}</span>
                  <span className="text-slate-300">·</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {v.visit_date}</span>
                  <span className="text-slate-300">·</span>
                  <span className="inline-flex items-center gap-1"><IndianRupee className="w-3 h-3" /> {Number(v.expense_amount || 0).toFixed(2)}</span>
                  {v.photo_ids?.length > 0 && (
                    <>
                      <span className="text-slate-300">·</span>
                      <span>{v.photo_ids.length} photos</span>
                    </>
                  )}
                </div>
              </div>
              <div className="text-[11px] text-slate-500 shrink-0">Tap to review</div>
            </div>
            <p className="text-xs text-slate-700 mt-2 line-clamp-2">{v.remarks || <span className="italic text-slate-400">No remarks</span>}</p>
          </article>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        {detail && <ReviewDialog visit={detail} onDone={(next) => { setDetail(null); load(); }} />}
      </Dialog>
    </div>
  );
}

function ReviewDialog({ visit, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (status) => {
    if (status === "rejected" && !note.trim()) { toast.error("Add a reason for the rejection"); return; }
    setBusy(true);
    try {
      await api.post(`/visits/${visit.id}/decision`, { status, decision_note: note });
      toast.success(`Visit ${status}`);
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const isPending = visit.status === "completed";

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-indigo-600" /> {visit.outlet_code} · {visit.visit_date}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div className="text-xs text-slate-500">
          Reported by <span className="font-semibold text-slate-800">{visit.op_manager_name}</span> · submitted {visit.completed_at?.slice(0,16).replace("T"," ")}
        </div>
        {visit.notes && <div className="text-xs text-slate-600 border-l-2 border-slate-200 pl-3">Pre-visit agenda: {visit.notes}</div>}
        <div>
          <div className="text-xs font-semibold text-slate-500 mb-1">Remarks</div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap rounded-lg bg-slate-50 border border-slate-100 p-3">{visit.remarks}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
            <div className="text-[10px] font-bold text-slate-500 uppercase">Expense</div>
            <div className="text-lg font-bold text-slate-900 inline-flex items-center"><IndianRupee className="w-4 h-4" />{Number(visit.expense_amount || 0).toFixed(2)}</div>
            {visit.receipt_file_id && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${visit.receipt_file_id}`} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:underline">
                <Paperclip className="w-3 h-3" /> View receipt
              </a>
            )}
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
            <div className="text-[10px] font-bold text-slate-500 uppercase">Photos</div>
            <div className="text-lg font-bold text-slate-900">{visit.photo_ids?.length || 0}</div>
          </div>
        </div>
        {visit.photo_ids?.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1.5">Field photos</div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {visit.photo_ids.map((pid) => (
                <a key={pid} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${pid}`} target="_blank" rel="noopener noreferrer" className="block rounded-md overflow-hidden border border-slate-200 aspect-square">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${pid}`} alt="Visit" className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          </div>
        )}
        {visit.decision_note && (
          <div className={`text-xs rounded p-2 ${visit.status === "approved" ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
            Admin note: {visit.decision_note}
          </div>
        )}
        {isPending && (
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1">Decision note (required to reject)</div>
            <Textarea data-testid="admin-visit-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Feedback for the Op Manager" className="min-h-16" />
          </div>
        )}
      </div>
      {isPending && (
        <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
          <Button data-testid="admin-visit-reject" variant="outline" disabled={busy} onClick={() => decide("rejected")} className="text-rose-700 border-rose-300 hover:bg-rose-50">
            <XCircle className="w-4 h-4 mr-1" /> Reject
          </Button>
          <Button data-testid="admin-visit-approve" disabled={busy} onClick={() => decide("approved")} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
          </Button>
        </DialogFooter>
      )}
    </DialogContent>
  );
}
